import React from 'react'
import { Box } from '@material-ui/core'
import { t } from 'ttag'
import MaterialReactTable, {
  MaterialReactTableProps,
  MRT_FullScreenToggleButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table'
import Hidden from '@material-ui/core/Hidden'
import variables from '../../styles/_variables.scss'
import { darkBlue, darkGray, darkerGray, gray } from '../../styles/material-ui-theme-v2'
import { tokens } from '../../styles/tokens'

interface YumTableProps extends MaterialReactTableProps {
  showFilters?: boolean
}

const YumTable = (props: YumTableProps) => {
  const { showFilters, ...otherProps } = props
  return (
    <MaterialReactTable
      autoResetPageIndex={false}
      muiTablePaperProps={{ elevation: 0 }}
      enableRowNumbers
      rowNumberMode="static"
      enableDensityToggle={false}
      enableFullScreenToggle={false}
      muiSearchTextFieldProps={{
        placeholder: t`Search`,
        style: { marginTop: '5px' },
      }}
      positionToolbarAlertBanner="bottom"
      enableFilters={true}
      renderToolbarInternalActions={({ table }) => (
        <Box display="flex" gridGap="xs">
          <MRT_ToggleGlobalFilterButton /*sx={{ color: `${darkGray} !important` }}*/ table={table} />
          {showFilters && <MRT_ToggleFiltersButton /*sx={{ color: `${darkGray} !important` }}*/ table={table} />}
          <Hidden smDown>
            <MRT_FullScreenToggleButton
              /*sx={{ color: `${darkGray} !important` }}*/
              table={table}
              onClick={() => {
                const { getState, setIsFullScreen } = table
                const { isFullScreen } = getState()

                setIsFullScreen(!isFullScreen)

                if (!isFullScreen) {
                  document.querySelectorAll<HTMLElement>('.nav')[0].style.zIndex = '0'
                  document.querySelectorAll<HTMLElement>('.layout__header')[0].style.zIndex = '0'
                } else {
                  document.querySelectorAll<HTMLElement>('.nav')[0].style.zIndex = '10'
                  document.querySelectorAll<HTMLElement>('.layout__header')[0].style.zIndex = '100'
                }
              }}
            />
          </Hidden>
        </Box>
      )}
      muiTableBodyProps={{
        sx: (theme) => ({
          '& > tr:hover > td': {
            backgroundColor: '#F2F8FF',
            '& .MuiFilledInput-root': {
              backgroundColor: 'white !important',
            },
          },
        }),
      }}
      muiTableHeadRowProps={{
        sx: {
          boxShadow: 'none',
        },
      }}
      muiTableHeadCellProps={{
        sx: (theme) => {
          return {
            paddingTop: '16px !important',
            paddingBottom: '16px !important',
            lineHeight: '24px !important',
            fontSize: '0.875rem !important',
            color: darkBlue,
            fontFamily: variables.font_family,
            borderColor: darkGray,
            // WCAG 2.2 SC 2.5.8 — enlarge MRT sort label + column-actions hit areas to 24×24
            // and leave enough horizontal gap that axe's "safe clickable space" diameter
            // stays at 24px. An earlier 4px gap reported only 17.2px safe diameter on
            // /admin/super-admin, so we bump both sides to 12px (24px total) to clear the
            // 6.8px shortfall comfortably.
            '& .MuiTableSortLabel-root': {
              minWidth: '24px',
              minHeight: '24px',
              marginRight: '12px',
              // The parent label group carries the focus ring; suppress the global
              // ring on the inner sort label so there's no double outline. Reveal the
              // sort arrow on keyboard focus (MRT only shows it on hover/active) so
              // the sort affordance is visible to keyboard users.
              '&:focus-visible, &.Mui-focusVisible': {
                outline: 'none',
                '& .MuiTableSortLabel-icon': {
                  opacity: 1,
                },
              },
              // Sort arrow must be a visible gray when shown (WCAG 2.2 SC 1.4.11).
              '& .MuiTableSortLabel-icon': {
                color: `${darkerGray} !important`,
              },
            },
            // material-react-table uses MUI v5, so the v4 theme's focus ring does not
            // reach it. The sort label itself is a narrow arrow slot, so ring the whole
            // header-label group instead — keyboard-only via :has(:focus-visible) —
            // for a visible focus indicator (WCAG 2.2 SC 2.4.7).
            '& .Mui-TableHeadCell-Content-Labels': {
              borderRadius: '4px',
              '&:has(.MuiTableSortLabel-root:focus-visible)': {
                outline: `2px solid ${tokens.brand.bluePressed}`,
                outlineOffset: '2px',
              },
            },
            '& .Mui-TableHeadCell-Content-Actions': {
              marginLeft: '12px',
            },
            '& .Mui-TableHeadCell-Content-Actions button': {
              minWidth: '28px',
              minHeight: '28px',
              // Column-actions icon must be a visible gray (WCAG 2.2 SC 1.4.11).
              color: darkerGray,
              // Replace MUI's pulsating focus ripple with the app's standard outline
              // (renders as a clean circular ring on this round button).
              '& .MuiTouchRipple-root': {
                display: 'none',
              },
              '&:focus-visible, &.Mui-focusVisible': {
                outline: `2px solid ${tokens.brand.bluePressed}`,
                outlineOffset: '2px',
              },
            },
          }
        },
      }}
      muiTableBodyCellProps={{
        sx: (theme) => ({
          paddingTop: '8px !important',
          paddingBottom: '8px !important',
          lineHeight: '40px !important',
          fontSize: '0.875rem !important',
          color: darkBlue,
          fontFamily: variables.font_family,
          borderColor: darkGray,
        }),
      }}
      muiTableDetailPanelProps={{
        sx: {
          backgroundColor: `${gray} !important`,
          padding: 0,
        },
      }}
      muiBottomToolbarProps={{
        sx: {
          boxShadow: 'none',
          '& .MuiTablePagination-select': {
            fontSize: '0.875rem !important',
            //paddingBottom: '2px',
            color: darkBlue,
            fontFamily: variables.font_family,
            borderColor: 'transparent !important',
            backgroundColor: 'white !important',
            borderRadius: '0px !important',
          },
          '& .MuiTablePagination-selectLabel': {
            fontSize: '0.875rem !important',
            color: darkBlue,
            position: 'relative',
            top: '-1px',
            fontFamily: variables.font_family,
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem !important',
            fontWeight: 'bold',
            color: darkBlue,
            fontFamily: variables.font_family,
          },
          '& .MuiTablePagination-menuItem': {
            display: 'block !important',
            textAlign: 'center !important',
          },
        },
      }}
      muiToolbarAlertBannerProps={{
        sx: (theme) => ({
          fontSize: '0.875rem !important',
          color: darkBlue,
          fontFamily: variables.font_family,
        }),
      }}
      muiTopToolbarProps={{
        sx: {
          '& > .MuiBox-root': {
            alignItems: 'center',
            padding: '0px',
            '.MuiDialog-scrollPaper &': {
              padding: '8px',
            },
          },
        },
      }}
      localization={{
        rowsPerPage: t`Rows per page`,
        noResultsFound: t`No matches found.`,
        noRecordsToDisplay: t`No matches found.`,
        search: t`Search`,

        clearSort: t`Clear sort`,
        clearFilter: t`Clear filter`,
        hideColumn: t`Hide {column} column`,
        showAllColumns: t`Show all columns`,
        sortByColumnAsc: t`Sort by {column} ascending`,
        sortByColumnDesc: t`Sort by {column} descending`,
        filterByColumn: t`Filter by {column}`,
        selectedCountOfRowCountRowsSelected: t`{selectedCount} of {rowCount} row(s) selected`,
        of: t`of`,

        // Non-empty aria-label values so MRT's internal icon buttons meet WCAG 2.2 SC 4.1.2.
        // These strings are used only as accessible names (not rendered visually), so translating them is safe.
        showHideSearch: t`Show/hide search`,
        showHideFilters: t`Show/hide filters`,
        clearSearch: t`Clear search`,
        sortedByColumnAsc: t`Sorted by {column} ascending`,
        sortedByColumnDesc: t`Sorted by {column} descending`,
        columnActions: t`{column} column actions`,
        goToFirstPage: t`Go to first page`,
        goToLastPage: t`Go to last page`,
        goToNextPage: t`Go to next page`,
        goToPreviousPage: t`Go to previous page`,
        toggleSelectAll: t`Toggle select all rows`,
        toggleSelectRow: t`Toggle select row`,
        toggleFullScreen: t`Toggle full screen`,

        expand: t`Expand`,
        expandAll: t`Expand all`,
        collapse: t`Collapse`,
        collapseAll: t`Collapse all`,
      }}
      {...otherProps}
    />
  )
}

export default YumTable
